import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import {
  allTcrFn,
  cisStatusOptionsFn,
  currencyOptionsFn,
  internalStatusOptionsFn,
  priorityOptionsFn,
} from "../../purchases/Utils";
import { toast } from "react-toastify";
import moment from "moment";
import { useAppContext, usePersistedState } from "../../../AppContext";
import mainAxios from "../../../services/mainAxios";
import PrimeTable from "../../PrimeTable/PrimeTable";
import DateRange from "../../global/DateRange";
import useGeneratePurchasesColumns from "../../purchases/columns/generatePurchasesColumns";
import func, { globalHandleChange } from "../../../services/functions";
import { defaultTableRows } from "../../../services/data";
import {  paymentPurchaseColumns } from "./PaymentOrderUtils";
import { Checkbox } from "primereact/checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faCheck } from "@fortawesome/free-solid-svg-icons";
import useShowInvoicePdf from "../../global/useShowInvoicePdf";
import { validateSameCurrency } from "./PaymentOrderUtils";

const PaymentPurchaseTable = ({ paymentOrder,onSuccess }) => {
  const { t } = useTranslation("translations");
  const {
    user: [user],
    selectedCompany: [selectedCompany],
  } = useAppContext();

  const [searchInvoices,setSearchInvoices]=useState([])
  const [selectedRows,setSelectedRows] = useState([])
  const company = user.companiesRights.find(
    (company) => company.id === parseInt(selectedCompany)
  );
  const [loading, setLoading] = useState(false);

    const [purchaseDates,setPurchaseDates]=React.useState({
    fromDate: func.getDateFromToday(-364),
    toDate: func.getDateFromToday(1),
  })

  const filterParams = {
    dataFrom: "DB",
    search: "einvoice",
    internalStatus: [],
    purchaseUnit: [],
    priority: [],
    currency: [],
    purchaseStatus: [],
    checkUnknownBusiness: false,
    purchaseStatus:["PARTIALLY_PAID","REFUSED","DELIVERED","ACCEPTED"],
    internalStatus:[],
    sellerTin:paymentOrder?.sellerBankAccountTransactional?.sellerNipt,
    paymentType:"NONCASH"
  };

  const defaultFilters = {
    params: { ...filterParams },
    fromDate: purchaseDates.fromDate,
    toDate: purchaseDates.toDate,
  };

  const [filters, setFilters] = useState(defaultFilters);

  const defaultSortCriteria = {
    field: "recordDateTime",
    direction: "DESC",
  };

  const [tableRows, setTableRows] = useState({
    currentPage: 1,
    rowsPerPage: 6,
    totalRows: 0,
    sortCriteria: defaultSortCriteria,
  });

  const tcrList = () => [];



  const allTcr = useMemo(() => allTcrFn(company), [selectedCompany, company.branches]);
  const internalStatusOptions = useMemo(() => internalStatusOptionsFn(t), [t]);
  const priorityOptions = useMemo(() => priorityOptionsFn(t), [t]);
  const currencyOptions = useMemo(() => currencyOptionsFn(), []);

  const handleParamsChange = (e, label, val) =>
    globalHandleChange({
      key: e?.target?.name ?? label,
      value: e?.target?.value ?? val,
      setState: setFilters,
      length: 0,
    });

  const getFilteredResults = ({
    data,
    pageSize = 6,
    page = defaultTableRows.currentPage,
    sortCriteria = [defaultSortCriteria],
  }) => {
    setLoading(true);
    const filterData = data ? { ...data } : { ...filters };
    filterData.params = {
      ...filterData.params,
      dataFrom: "DB",
    };

    let purchaseUnit =
      filterData.params.purchaseUnit.length === 0
        ? tcrList().map((tcr) => tcr.value).join(",")
        : filterData.params.purchaseUnit.join(",");

    let reqParams = {};
    Object.keys(filterData.params).forEach((param) => {
      if (filterData.params[param] !== "" && param !== "purchaseUnit") {
        if (
          param === "priority" ||
          param === "currency" ||
          param === "internalStatus" ||
          param === "purchaseStatus"
        ) {
          if (filterData.params[param]?.length > 0) {
            reqParams[param] = filterData.params[param].join(",");
          }
        } else {
          reqParams[param] = filterData.params[param];
        }
      }
    });

    if (purchaseUnit !== "") {
      reqParams.purchaseUnit = purchaseUnit;
    }

    return mainAxios
      .post("endpoint/v2/getMyPurchases", {
        ...filterData,
        fromDate: purchaseDates.fromDate,
        toDate: purchaseDates.toDate,
        params: { ...reqParams },
        value: null,
        pagination: {
          pageSize: pageSize,
          pageNumber: page,
        },
        nuis: user.companiesRights.find(
          (company) => company.id === parseInt(selectedCompany)
        )?.nuis,
        sortCriteria,
      })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          setSearchInvoices(res.data.content);
          setTableRows((tableRows) => ({
            ...tableRows,
            totalRows: res?.data?.totalSize,
          }));
          return true;
        }
        return false;
      })
      .catch(() => {
        setLoading(false);
        return false;
      });
  };

  useEffect(() => {
    if (!(purchaseDates?.fromDate && purchaseDates?.toDate)) {
      return;
    }
    getFilteredResults({ data: defaultFilters });
  }, [purchaseDates.reloadData, purchaseDates?.fromDate, purchaseDates?.toDate]);

  const { showInvoicePdf,pdfEmbed,loadingPdf } = useShowInvoicePdf({loadingIdKey:"fic"})

  const cisStatusOptions = useMemo(()=>cisStatusOptionsFn(t,{}),[t])

  const columns = useGeneratePurchasesColumns({
    loadingIdKey:"fic",
    loadingPdf,
    showInvoicePdf,
    dataFrom: "DB",
    allTcr,
    internalStatusOptions,
    priorityOptions,
    currencyOptions,
    tcrList,
    filters,
    handleParamsChange,
    getFilteredResults,
    compact: true,
    searchPurchaseTable:true,
    cisStatusOptions
  });

  const parsedColumns = useMemo(
    () => paymentPurchaseColumns
      .map((field) => columns.find((col) => col.field === field))
      .filter(Boolean),
    [paymentPurchaseColumns, columns]
  );


  const [selectedColumns, setSelectedColumns] = usePersistedState(
      parsedColumns.map((col) => ({field:col.field,show:!!col.default,...(col?.persisted?{persisted:true}:{})})),
      "searchPurchaseCols3"
  );

  const customDateRanges = [
    {
      label: t("common.thisYear"),
      value: "thisYear",
      onDateChange: () => {
        let startDate = moment().startOf("year").toDate();
        let endDate = moment().endOf("day").toDate();
        return [startDate, endDate];
      },
    },
    {
      label: t("common.lastYear"),
      value: "lastYear",
      onDateChange: () => {
        let startDate = moment().subtract(1, "year").startOf("year").toDate();
        let endDate = moment().subtract(1, "year").endOf("year").toDate();
        return [startDate, endDate];
      },
    },
  ];



  const headerLeft = (
    <div className="d-flex align-items-center" >
      <div className="mx-3" style={{fontSize:16,fontWeight:500}}>{selectedRows.length} fatura te perzgjedhura</div>
      {selectedRows.length>0 && <button className="btn btn-sm btn-light" onClick={()=>setSelectedRows([])}>Clear All</button>}
      {selectedRows.length>0 && <button className="btn btn-sm btn-primary ml-3" onClick={()=>onSuccess(selectedRows)}>
        <FontAwesomeIcon icon={faCheck}/>
          <span className="ml-2"> Done</span>
        </button>}
    </div>

  );

  const headerRight=(
    <div className="mr-2">
      <DateRange
        fromDate={purchaseDates.fromDate}
        toDate={purchaseDates.toDate}
        customDateRanges={customDateRanges}
        onChange={(dates) => {
          let fromDate = dates[0];
          let toDate = dates[1];
          setFilters((filters) => ({
            ...filters,
            fromDate,
            toDate,
          }));
          setPurchaseDates((state) => ({ ...state, fromDate, toDate }));
        }}
        position="right"
    />
    </div>

  )



    const multiSelectInvoicesFilter=useMemo(()=>{
        const selectedRowsIds=selectedRows.map(row=>row.id)
        const isChecked=searchInvoices.every(invoice=>(selectedRowsIds ?? []).includes(invoice.id))

        return <Checkbox
            onChange={
                (e) => {
                    const currentPageIds=searchInvoices.map(inv=>inv.id)
                    if(e.checked){
                        setSelectedRows((selectedRows)=>{
                          let rows=[...selectedRows]
                          rows=rows.filter(row=>!currentPageIds.includes(row.id))
                          return [...rows,...searchInvoices]
                        })
                    }else{
                        setSelectedRows(selectedRows=>{selectedRows=selectedRows.filter(row=>!currentPageIds.includes(row.id));return selectedRows })
                    }
                }
            }
            style={{marginLeft:12}}
            checked={isChecked}
        />
    }, [searchInvoices, selectedRows])

    const multiSelectInvoicesBody=(newInvoice)=>{
        return <Checkbox
            onChange={
            (e) => {
                const isCurrentlySelected = !!selectedRows.find(row=>row.id==newInvoice?.id)
                if(isCurrentlySelected) {
                    setSelectedRows((state)=> state.filter(row=>row.id!==newInvoice?.id))
                    return
                }
                const updatedState = [...selectedRows, newInvoice]
                if (!validateSameCurrency(updatedState)) {
                    toast.error(t('paymentOrder.differentCurrency'), {
                        containerId: 'all_toast',
                    });
                    return
                }

                setSelectedRows(updatedState)
            }
        }
        checked={!!selectedRows.find(row=>row.id==newInvoice?.id)}
        />
    }

  return (
    <>
      <PrimeTable
        loading={loading}
        showFilters={true}
        reorderableColumns={true}
        filters={[filters, setFilters]}
        likeValues={false}
        onFilter={(data) => setFilters(data.filters)}
        compact={true}
        headerButtonsLeft={headerLeft}
        headerButtonsRight={headerRight}
        defaultFilters={defaultFilters}
        tableRows={[tableRows, setTableRows]}
        value={searchInvoices ?? []}
        columns={parsedColumns}
        showSelectColumns={true}
        dataKey="id"
        gridKey="searchPurchaseCols3"
        selectedColumns={[selectedColumns, setSelectedColumns]}
        onRemoteDataChange={(remoteData) => {
          const updatedFilters = remoteData.filters ?? filters;
          const updatedTableData = remoteData.tableRows ?? tableRows;
          let { currentPage, rowsPerPage, sortCriteria } = updatedTableData;
          sortCriteria = sortCriteria ? [sortCriteria] : [];
          getFilteredResults({
            data: updatedFilters,
            pageSize: rowsPerPage,
            page: currentPage,
            sortCriteria,
          });
        }}
        defaultSortCriteria={defaultSortCriteria}
      >
        <Column
            filter={true}
            showFilterMenu={false}
            showClearButton={false}
            filterElement={multiSelectInvoicesFilter}
            body={multiSelectInvoicesBody}
            headerStyle={{ width: '3rem' }} frozen />
      </PrimeTable>
      {pdfEmbed}
    </>
  );
};

export default PaymentPurchaseTable;